import { createSelector } from '@ngrx/store';
import * as fromApp from '../reducers/app.reducer';
import * as fromDocument from '../reducers/document.reducer';
import * as fromUser from '../reducers/user.reducer';
import { IFile } from '@app-root-interfaces';
import { IFolderTree } from '../../../main/interfaces/folder-tree.interface';

const selectDocument = (state: fromApp.AppState) => state.document;
const selectUser = (state: fromApp.AppState) => state.user;

function createFolderTree(state: fromDocument.State, fileId: string): IFolderTree {
    if (state.folders[fileId]) {
        if (state.folders[fileId].folders) {
            return {
                name: state.folders[fileId].name,
                id: fileId,
                isExpanded: state.path.indexOf(fileId) >= 0,
                childrenLoaded: true,
                childs: [...state.folders[fileId].folders.map((folder) => createFolderTree(state, folder.id))]
            };
        }
        return {
            name: state.folders[fileId].name,
            id: fileId,
            isExpanded: false,
            childrenLoaded: false,
            childs: []
        };
    }
    return null;
}

function createFolderTreeWithoutExpanded(state: fromDocument.State, fileId: string): IFolderTree {
    if (state.folders[fileId]) {
        if (state.folders[fileId].folders) {
            return {
                name: state.folders[fileId].name,
                id: fileId,
                isExpanded: true,
                childs: [
                    ...state.folders[fileId].folders.map((folder) => createFolderTreeWithoutExpanded(state, folder.id))
                ]
            };
        }
        return {
            name: state.folders[fileId].name,
            id: fileId,
            isExpanded: true,
            childs: []
        };
    }
    return null;
}

export const selectDocumentFolders = createSelector(selectDocument, (state: fromDocument.State) => state.folders);

export const selectDocumentFolder = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State) => {
        if (!documentState.drive) {
            return null;
        }
        const currentFolder = documentState.path[documentState.path.length - 1];
        const folder = currentFolder ? documentState.folders[currentFolder] : null;

        if (folder && folder.files) {
            return {
                ...folder,
                folders: folder.folders
                    ? folder.folders.map((f) => documentState.folders[f.id] || null).filter((f) => !!f)
                    : [],
                files: folder.files
                    .map((file: IFile) => documentState.files[file.id] || null)
                    .filter((f) => !!f)
                    .map((file: IFile) => addFavoriteTag(file, userState))
            };
        }
        return folder;
    }
);

export const selectDocumentCurrentFolder = createSelector(
    selectDocument,
    (state: fromDocument.State) => state.folders[state.path[state.path.length - 1]]
);

export const selectDocumentFavourites = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State) => {
        if (!userState.currentUser) {
            return [];
        }
        if (
            !userState.currentUser ||
            !userState.currentUser.favoritDocuments ||
            !userState.currentUser.favoritDocuments.length
        ) {
            return [];
        }

        return userState.currentUser.favoritDocuments
            .map((id: string) => {
                const favFile = Object.values(documentState.files).find((file) => file.favoriteId === id);
                if (!favFile) {
                    return null;
                }

                return {
                    ...favFile,
                    isFavourite: true
                };
            })
            .filter((file) => !!file);
    }
);

export const selectDocumentRecentlySeen = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State) => {
        if (!userState.currentUser) {
            return [];
        }
        const files = userState.currentUser.recentDocuments.reduce((arr: IFile[], documentKey: string) => {
            if (documentState.files[documentKey]) {
                const file = { ...documentState.files[documentKey] };
                return [...arr, addFavoriteTag(file, userState)];
            }
            return arr;
        }, []);
        return files;
    }
);

export const selectDocumentBasicFiles = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State) => {
        let basicFiles = [...documentState.basicFiles];
        if (basicFiles && basicFiles.length > 0) {
            basicFiles = basicFiles
                .map((file: IFile) => documentState.files[file.id] || null)
                .filter((f) => !!f)
                .map((file: IFile) => addFavoriteTag(file, userState));
        }
        return basicFiles;
    }
);

export const selectDocumentBasicFolder = createSelector(
    selectDocument,
    (state: fromDocument.State) => state.basicFolder
);

export const selectDocumentFiles = createSelector(selectDocument, (state: fromDocument.State) => state.files);

export const selectDocumentPath = createSelector(selectDocument, (state: fromDocument.State) =>
    state.path.map((node) => state.folders[node])
);

export const selectDocumentPathRaw = createSelector(selectDocument, (state: fromDocument.State) => state.path);

export const selectDocumentFolderTree = createSelector(selectDocument, (state: fromDocument.State) =>
    state.folders && state.folders[state.drive] ? createFolderTree(state, state.folders[state.drive].id) : null
);

export const selectDocumentFolderTreeForFolderSelect = createSelector(selectDocument, (state: fromDocument.State) =>
    state.folders && state.folders[state.drive]
        ? createFolderTreeWithoutExpanded(state, state.folders[state.drive].id)
        : null
);

export const selectDocumentSearch = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State) =>
        documentState.search.map((file: IFile) => {
            if (
                !userState.currentUser ||
                !userState.currentUser.favoritDocuments ||
                userState.currentUser.favoritDocuments.length === 0
            ) {
                return file;
            }
            if (userState.currentUser.favoritDocuments.includes(file.favoriteId)) {
                file.isFavourite = true;
            }
            return file;
        })
);

export const selectDocumentFileById = createSelector(
    selectDocument,
    selectUser,
    (documentState: fromDocument.State, userState: fromUser.State, props: { id: string }) => {
        return addFavoriteTag(documentState.files[props.id], userState);
    }
);

export const selectDocumentFolderById = createSelector(
    selectDocument,
    (documentState: fromDocument.State, props: { id: string }) => {
        return (
            documentState.folders[props.id] ||
            (documentState.basicFolder.id === props.id && documentState.basicFolder) ||
            undefined
        );
    }
);

export const selectDocumentDrive = createSelector(
    selectDocument,
    (state: fromDocument.State) => state.folders[state.drive]
);

export const selectDocumentAllDriveIds = createSelector(
    selectDocument,
    (state: fromDocument.State) => state.driveIdCollection
);

export const selectDocumentToEdit = createSelector(selectDocument, (state: fromDocument.State) => state.toEdit);

export const selectDocumentEditLoading = createSelector(
    selectDocument,
    (state: fromDocument.State) => state.editLoading
);

export const selectDocumentAllDrives = createSelector(selectDocument, (state: fromDocument.State) => state.allDrives);

export const selectDocumentAllArchiveDrives = createSelector(selectDocument, (state: fromDocument.State) =>
    state.allDrives ? state.allDrives.filter((drive) => !!drive.driveName) : []
);

function addFavoriteTag(file: IFile, userState: fromUser.State) {
    if (!file) {
        return file;
    }
    if (
        userState.currentUser &&
        userState.currentUser.favoritDocuments &&
        userState.currentUser.favoritDocuments.length > 0 &&
        userState.currentUser.favoritDocuments.indexOf(file.favoriteId) >= 0
    ) {
        return {
            ...file,
            isFavourite: true
        };
    }
    return file;
}
